export interface SeoMeta {
    title: string;
    description: string;
    image?: string;
    robots?: string;
}

const config = {
    brandSuffix: "360° Viewer",
    siteRoot: "https://www.360-javascriptviewer.com",
    twitterHandle: "@360viewer",
    organizationId: "https://www.360-javascriptviewer.com/#organization",
    websiteId: "https://www.360-javascriptviewer.com/#website",
};

function buildDocumentTitle(pageTitle: string): string {
    const normalized = pageTitle.trim();
    if (normalized.includes("|")) {
        return normalized.length <= 60 ? normalized : normalized.slice(0, 60).trim();
    }

    const suffix = ` | ${config.brandSuffix}`;
    const combined = `${normalized}${suffix}`;
    if (combined.length <= 60) {
        return combined;
    }

    const budget = 60 - suffix.length;
    return `${normalized.slice(0, budget).trim()}${suffix}`;
}

export function buildCanonicalUrl(path: string): string {
    const pathWithoutTracking = path.split(/[?#]/, 1)[0] || "/";
    const normalizedPath = `/${pathWithoutTracking}`.replace(/\/{2,}/g, "/").replace(/\/$/, "");

    if (!normalizedPath) {
        return `${config.siteRoot}/`;
    }

    return `${config.siteRoot}${normalizedPath}`;
}

export default function ({title, description, image, robots}: SeoMeta = {title: "", description: "", image: ""}) {
    const ogImage = image || "logo.png";
    const route = useRoute();
    const documentTitle = buildDocumentTitle(title);
    const canonicalUrl = buildCanonicalUrl(route.path);

    const meta = [
        {hid: "description", name: "description", content: description},
        {hid: "og:title", property: "og:title", content: documentTitle},
        {hid: "og:description", property: "og:description", content: description},
        {hid: "og:image", property: "og:image", content: `${config.siteRoot}/images/${ogImage}`},
        {hid: "og:url", property: "og:url", content: canonicalUrl},
        {hid: "og:type", property: "og:type", content: "website"},
        {hid: "twitter:title", name: "twitter:title", content: documentTitle},
        {hid: "twitter:description", name: "twitter:description", content: description},
        {hid: "twitter:image", name: "twitter:image", content: `${config.siteRoot}/images/${ogImage}`},
        {hid: "twitter:creator", name: "twitter:creator", content: config.twitterHandle},
        {hid: "twitter:site", name: "twitter:site", content: config.twitterHandle},
        {hid: "twitter:card", name: "twitter:card", content: "summary_large_image"},
    ];

    if (robots) {
        meta.push({hid: "robots", name: "robots", content: robots});
    }

    useHead({
        title: documentTitle,
        meta,
        link: [
            {hid: "canonical", rel: "canonical", href: canonicalUrl},
        ],
        script: [
            {
                type: "application/ld+json",
                children: JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "WebPage",
                    "@id": `${canonicalUrl}#webpage`,
                    url: canonicalUrl,
                    name: documentTitle,
                    description,
                    isPartOf: {"@id": config.websiteId},
                    about: {"@id": config.organizationId},
                }),
            },
        ],
    });
}
